import type { PluginDefinition, PluginGroup, PluginNavigationItem } from '@/plugins/types';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';
import { useWorkspaceMemberStore } from '@/stores/useWorkspaceMemberStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { canAccessPlugin } from '@/auth/roleUtils';
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities';
import { usePluginsByGroup } from './usePluginsByGroup';

export const SUITE_PLUGIN_GROUPS: Array<Exclude<PluginGroup, 'core'>> = ['analysis', 'data', 'admin'];

export const SUITE_PLUGIN_GROUP_LABELS: Record<Exclude<PluginGroup, 'core'>, string> = {
  analysis: 'Wissen & Analyse',
  data: 'Datenmanagement',
  admin: 'Administration',
};

export function getPrimaryPluginNavigation(plugin: PluginDefinition): PluginNavigationItem | null {
  return plugin.navigation?.find((item) => !item.placement || item.placement === 'main')
    ?? plugin.navigation?.[0]
    ?? null;
}

export function useVisibleSuitePlugins() {
  const { currentUser } = useOrgMemberStore();
  const { members } = useWorkspaceMemberStore();
  const { activeWorkspace } = useWorkspaceStore();
  const { isMachineAware } = useWorkspaceCapabilities();
  const orgRole = currentUser?.role ?? null;
  const workspaceRole = (() => {
    if (!currentUser?.userId || !activeWorkspace) return null;
    const member = members.find((entry) => entry.userId === currentUser.userId);
    return member?.role ?? null;
  })();

  const analysisPlugins = usePluginsByGroup('analysis');
  const dataPlugins = usePluginsByGroup('data');
  const adminPlugins = usePluginsByGroup('admin');

  const filterVisible = (plugins: PluginDefinition[]) =>
    plugins.filter(
      (plugin) => (
        plugin.id !== 'machine-registry' || isMachineAware
      ) && canAccessPlugin(plugin, orgRole, workspaceRole) && getPrimaryPluginNavigation(plugin) !== null
    );

  const visibleAnalysis = filterVisible(analysisPlugins);
  const visibleData = filterVisible(dataPlugins);
  const visibleAdmin = filterVisible(adminPlugins);

  return {
    visibleAnalysis,
    visibleData,
    visibleAdmin,
    visibleByGroup: {
      analysis: visibleAnalysis,
      data: visibleData,
      admin: visibleAdmin,
    } satisfies Record<Exclude<PluginGroup, 'core'>, PluginDefinition[]>,
    totalVisible: visibleAnalysis.length + visibleData.length + visibleAdmin.length,
  };
}